import { useMemo } from 'react';
import type { EWayBillRecord } from '@/api/compliance.api';

function formatINR(n: number): string {
  return `\u20B9${n.toLocaleString('en-IN')}`;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
}

function timeRemainingShort(validUntil: string): { text: string; level: 'red' | 'amber' | 'green' } {
  const now = Date.now();
  const end = new Date(validUntil).getTime();
  const diff = end - now;
  if (diff <= 0) return { text: 'Expired', level: 'red' };
  const hrs = Math.floor(diff / (1000 * 60 * 60));
  const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (hrs < 2) return { text: `${hrs > 0 ? `${hrs}h ` : ''}${mins}m left`, level: 'red' };
  if (hrs < 6) return { text: `${hrs}h ${mins}m left`, level: 'amber' };
  if (hrs > 24) return { text: `${Math.floor(hrs / 24)}d ${hrs % 24}h left`, level: 'green' };
  return { text: `${hrs}h ${mins}m left`, level: 'green' };
}

interface Props {
  ewbs: EWayBillRecord[];
  summary: { active: number; expiringToday: number; expired: number; cancelled: number };
}

export default function DashboardTab({ ewbs, summary }: Props) {
  const statusChart = useMemo(() => {
    const total = ewbs.length || 1;
    return [
      { label: 'Active', count: summary.active, color: 'bg-emerald-500', textColor: 'text-emerald-700' },
      { label: 'Expired', count: summary.expired, color: 'bg-red-500', textColor: 'text-red-700' },
      { label: 'Cancelled', count: summary.cancelled, color: 'bg-gray-400', textColor: 'text-gray-600' },
    ];
  }, [ewbs.length, summary]);

  const activeTimeline = useMemo(() => {
    return ewbs
      .filter((e) => e.status === 'ACTIVE')
      .sort((a, b) => new Date(a.validUntil).getTime() - new Date(b.validUntil).getTime());
  }, [ewbs]);

  const transporterStats = useMemo(() => {
    const map = new Map<string, { count: number; totalValue: number; totalDist: number }>();
    ewbs.forEach((e) => {
      const key = e.transporterName || 'Self Transport';
      const existing = map.get(key) || { count: 0, totalValue: 0, totalDist: 0 };
      existing.count += 1;
      existing.totalValue += e.totalValue;
      existing.totalDist += e.distanceKm;
      map.set(key, existing);
    });
    return Array.from(map.entries())
      .map(([name, stats]) => ({
        name,
        count: stats.count,
        totalValue: stats.totalValue,
        avgDistance: Math.round(stats.totalDist / stats.count),
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [ewbs]);

  const recentActivity = useMemo(() => {
    return ewbs
      .slice()
      .sort((a, b) => new Date(b.generatedDate).getTime() - new Date(a.generatedDate).getTime())
      .slice(0, 10)
      .map((e) => {
        if (e.status === 'CANCELLED') {
          return { text: `EWB ${e.ewbNumber} cancelled — ${e.cancelReason || 'Not specified'}`, time: e.cancelledAt || e.generatedDate, type: 'cancel' as const };
        }
        if (e.status === 'EXPIRED') {
          return { text: `EWB ${e.ewbNumber} expired`, time: e.validUntil, type: 'expire' as const };
        }
        return { text: `EWB ${e.ewbNumber} generated for ${e.invoiceNumber}`, time: e.generatedDate, type: 'generate' as const };
      });
  }, [ewbs]);

  return (
    <div className="space-y-6">
      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="text-xs text-gray-500">Active EWBs</span>
          </div>
          <div className="text-2xl font-semibold text-gray-900">{summary.active}</div>
          <div className="mt-1 text-xs text-gray-500">Currently valid</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full bg-amber-500" />
            <span className="text-xs text-gray-500">Expiring Today</span>
          </div>
          <div className="text-2xl font-semibold text-gray-900">{summary.expiringToday}</div>
          <div className="mt-1 text-xs text-gray-500">Needs attention</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full bg-red-500" />
            <span className="text-xs text-gray-500">Expired</span>
          </div>
          <div className="text-2xl font-semibold text-gray-900">{summary.expired}</div>
          <div className="mt-1 text-xs text-gray-500">Past validity</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full bg-gray-400" />
            <span className="text-xs text-gray-500">Cancelled</span>
          </div>
          <div className="text-2xl font-semibold text-gray-900">{summary.cancelled}</div>
          <div className="mt-1 text-xs text-gray-500">This month</div>
        </div>
      </div>

      {/* Status Chart + Timeline Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* EWB by Status Chart */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h4 className="text-sm font-semibold text-gray-900 mb-4">EWB by Status</h4>
          <div className="space-y-4">
            {statusChart.map((item) => (
              <div key={item.label}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-gray-600">{item.label}</span>
                  <span className={`text-xs font-semibold ${item.textColor}`}>{item.count}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all duration-500 ${item.color}`}
                    style={{ width: `${Math.round((item.count / (ewbs.length || 1)) * 100)}%`, minWidth: item.count > 0 ? '8px' : '0' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Validity Timeline */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h4 className="text-sm font-semibold text-gray-900 mb-4">Validity Timeline</h4>
          {activeTimeline.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-sm text-gray-400">
              No active EWBs today
            </div>
          ) : (
            <div className="space-y-3">
              {activeTimeline.map((ewb) => {
                const tr = timeRemainingShort(ewb.validUntil);
                const levelBg = tr.level === 'red' ? 'bg-red-50 border-red-200' : tr.level === 'amber' ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200';
                const levelText = tr.level === 'red' ? 'text-red-700' : tr.level === 'amber' ? 'text-amber-700' : 'text-emerald-700';
                const levelDot = tr.level === 'red' ? 'bg-red-500' : tr.level === 'amber' ? 'bg-amber-500' : 'bg-emerald-500';
                return (
                  <div key={ewb.id} className={`flex items-center justify-between px-3 py-2.5 rounded-lg border ${levelBg} text-xs`}>
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className={`w-2 h-2 rounded-full shrink-0 ${levelDot}`} />
                      <span className="font-mono font-medium text-gray-900 whitespace-nowrap">{ewb.ewbNumber}</span>
                      <span className="text-gray-500 whitespace-nowrap truncate">{ewb.invoiceNumber}</span>
                      <span className="text-gray-500 truncate hidden sm:inline">{ewb.customerName}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 ml-2">
                      <span className="text-gray-500 whitespace-nowrap">{formatDateTime(ewb.validUntil)}</span>
                      <span className={`font-semibold ${levelText} whitespace-nowrap`}>{tr.text}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Top Transporters + Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Transporters */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h4 className="text-sm font-semibold text-gray-900 mb-4">Top Transporters</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left font-medium text-gray-500 whitespace-nowrap">Transporter</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-500 whitespace-nowrap">EWBs</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500 whitespace-nowrap">Total Value</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500 whitespace-nowrap">Avg Dist</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {transporterStats.map((t, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-900 font-medium whitespace-nowrap">{t.name}</td>
                    <td className="px-3 py-2 text-center text-gray-600 whitespace-nowrap">{t.count}</td>
                    <td className="px-3 py-2 text-right text-gray-600 whitespace-nowrap">{formatINR(t.totalValue)}</td>
                    <td className="px-3 py-2 text-right text-gray-600 whitespace-nowrap">{t.avgDistance} km</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Activity Feed */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h4 className="text-sm font-semibold text-gray-900 mb-4">Recent Activity</h4>
          <div className="space-y-0">
            {recentActivity.map((act, i) => (
              <div key={i} className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0 text-xs">
                <div className="flex items-center gap-2 min-w-0">
                  {act.type === 'cancel' ? (
                    <i className="ri-close-circle-line text-red-500 shrink-0" />
                  ) : act.type === 'expire' ? (
                    <i className="ri-timer-line text-red-400 shrink-0" />
                  ) : (
                    <i className="ri-checkbox-circle-line text-emerald-500 shrink-0" />
                  )}
                  <span className="text-gray-700 truncate">{act.text}</span>
                </div>
                <span className="text-gray-400 shrink-0 ml-2 whitespace-nowrap">{formatDateTime(act.time)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}